import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForceLanguage } from 'hooks/use-force-language';
import { DEFAULT_LANGUAGE } from 'i18n';
import { LogoMark } from 'components/global/LogoMark';
import config from 'config';
import { cn } from 'lib/utils';
import { AuthProofPanel } from './AuthProofPanel';

/** The public site; the footer's privacy and terms pages live there. */
const SITE_URL = config.landing_url;

interface AuthSplitPanelProps {
  /** Heading shown in the phone's navy hero and above the form on desktop. */
  title: string;
  /** Optional line under the heading; left out on the first sign-in / sign-up step. */
  subtitle?: string;
  children: React.ReactNode;
  showProofPanel?: boolean;
}

function Brand({ className }: { className?: string }) {
  return (
    <a
      href={SITE_URL}
      className={cn(
        'inline-flex items-center gap-2.5 rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      aria-label="Examing"
    >
      <LogoMark size={28} className="rounded-lg" />
      <span className="font-display text-[17px] font-extrabold tracking-[-0.02em] lg:text-lg">Examing</span>
    </a>
  );
}

/** "© 2026 Examing   Maxfiylik   Shartlar": the links open the public site. */
function AuthFooter() {
  const { t } = useTranslation();
  return (
    <footer className="flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 text-[12.5px] text-muted-foreground">
      <span>© {new Date().getFullYear()} Examing</span>
      <a href={`${SITE_URL}/privacy`} className="hover:text-foreground hover:underline">
        {t('auth.privacy')}
      </a>
      <a href={`${SITE_URL}/terms`} className="hover:text-foreground hover:underline">
        {t('auth.terms')}
      </a>
    </footer>
  );
}

/**
 * Sign-in and sign-up pages: on phones a navy hero over a white form, on
 * desktop the brand row, then a 380px form column on the page background.
 * Auth is always in Uzbek.
 */
export function AuthSplitPanel({ title, subtitle, children, showProofPanel = true }: AuthSplitPanelProps) {
  useForceLanguage(DEFAULT_LANGUAGE);

  return (
    <div
      className={cn(
        'grid min-h-screen w-full bg-card text-foreground lg:bg-background',
        showProofPanel && 'lg:grid-cols-[1fr_minmax(0,460px)]',
      )}
    >
      {/* Form column */}
      <div className="flex min-h-screen flex-col">
        {/* Phone: navy hero with the heading */}
        <div className="rounded-b-2xl bg-[#0A1628] px-5 pb-7 pt-7 text-white lg:hidden">
          <Brand />
          <h1 className="mt-7 text-center text-[26px] font-bold leading-[1.2] tracking-[-0.02em]">{title}</h1>
          {subtitle && <p className="mt-1.5 text-center text-[15px] text-white/70">{subtitle}</p>}
        </div>

        {/* Desktop: brand row */}
        <div className="hidden px-10 pt-10 lg:block">
          <Brand />
        </div>

        <div className="flex flex-1 flex-col px-5 py-6 lg:px-10 lg:pb-8 lg:pt-[88px]">
          <div className="mx-auto w-full max-w-[380px]">
            <div className="mb-7 hidden text-center lg:block">
              <h1 className="text-[30px] font-bold leading-[1.15] tracking-[-0.02em]">{title}</h1>
              {subtitle && <p className="mt-2 text-[15px] text-muted-foreground">{subtitle}</p>}
            </div>
            {children}
          </div>
        </div>

        <div className="px-5 pb-6 lg:px-10 lg:pb-8">
          <AuthFooter />
        </div>
      </div>

      {/* Product-proof panel — wide screens only */}
      {showProofPanel && <AuthProofPanel className="lg:flex" />}
    </div>
  );
}

export default AuthSplitPanel;
